/**
 *
 * {@link https://rebilly.github.io/RebillyAPI/#tag/Subscriptions/paths/~1subscriptions~1{id}/put Subscriptions put}
 *
 */
import EntityModel from '@/common/models/entity-model';
import OrderItemModel from '@/common/models/order-item-model';
import OrderLineItemSubtotalModel from '@/common/models/order-line-item-subtotal-model';
import ContactModel from '@/common/models/contact-model';
import CustomerModel from '@/common/models/customer-model';
import InvoiceTimeShiftModel from '@/common/models/invoice-time-shift-model';
import PeriodAnchorInstructionModel from '@/common/models/period-anchor-instruction-model';


export default class OrderModel extends EntityModel {
    static Statuses = {
        active: 'active',
        pending: 'pending',
        canceled: 'canceled',
        churned: 'churned',
        paused: 'paused',
        suspended: 'suspended',
    };

    /**
     * These statuses are legacy values that are not used anymore.
     * @type {{inactive: string, cancelledActive: string,
     * future: string, active: string, cancelled: string}}
     */
    static LegacyStatuses = {
        active: 'Active',
        future: 'Will become active at a future date',
        cancelledActive: 'Active but set to cancel at next rebill date',
        cancelled: 'Cancelled',
        inactive: 'Inactive',
    };

    static BillingStatuses = {
        unpaid: 'unpaid',
        pastDue: 'past-due',
        delinquent: 'delinquent',
        paid: 'paid',
        voided: 'voided',
        refunded: 'refunded',
        disputed: 'disputed',
        abandoned: 'abandoned',
    };

    static Types = {
        // Order matters, the should is a default value.
        subscription: 'subscription-order',
        oneTime: 'one-time-order',
    };

    static StartTimeTypes = {
        now: 'now',
        specific: 'specific',
    };

    hydrate({
        id = null,
        orderType = null,
        customerId = null,
        items = null,
        websiteId = null,
        deliveryAddress = null,
        billingAddress = null,
        autopay = true,
        trial = null,
        inTrial = false,
        riskMetadata = null,
        startTime = null,
        createdTime = null,
        renewalTime = null,
        canceledTime = null,
        endTime = null,
        status = null,
        billingStatus = null,
        customFields = null,
        invoiceTimeShift = null,
        recurringInterval = null,
        lineItemSubtotal = null,
        cancelDescription = null,
        ...otherFields
    } = {}) {
        this.id = id;
        this.orderType = orderType;
        this.customerId = customerId;
        this.items = items || [new OrderItemModel()];
        this.websiteId = websiteId;
        this.deliveryAddress = new ContactModel(deliveryAddress || {});
        this.billingAddress = billingAddress ? new ContactModel(billingAddress || {}) : null;
        this.autopay = autopay;
        this.trial = trial;
        this.inTrial = inTrial;
        this.riskMetadata = riskMetadata;
        this.startTime = startTime;
        this.endTime = endTime;
        this.createdTime = createdTime;
        this.canceledTime = canceledTime;
        this.renewalTime = renewalTime;
        this.customFields = customFields;
        this.status = OrderModel.mapLegacyStatus(status);
        this.$internalStatus = status;
        this.billingStatus = billingStatus;
        this.invoiceTimeShift = new InvoiceTimeShiftModel(invoiceTimeShift || {});
        this.lineItemSubtotal = new OrderLineItemSubtotalModel(lineItemSubtotal || {});

        this.recurringInterval = null;
        if (recurringInterval) {
            const {periodAnchorInstruction} = recurringInterval;
            this.recurringInterval = new PeriodAnchorInstructionModel(
                periodAnchorInstruction || recurringInterval,
            );
        }

        this.cancelDescription = cancelDescription;

        this.customer = EntityModel.fromEmbedded({
            fields: otherFields,
            entityName: 'customer',
            model: CustomerModel,
        });
    }

    /**
     * Convert a legacy status to a current one if needed.
     * See [GWY-13320]
     * @param {string} status
     * @returns {string}
     */
    static mapLegacyStatus(status) {
        switch (status) {
            case OrderModel.LegacyStatuses.active:
                return OrderModel.Statuses.active;
            case OrderModel.LegacyStatuses.cancelled:
                return OrderModel.Statuses.churned;
            case OrderModel.LegacyStatuses.future:
                return OrderModel.Statuses.pending;
            case OrderModel.LegacyStatuses.inactive:
                return OrderModel.Statuses.churned;
            case OrderModel.LegacyStatuses.cancelledActive:
                return OrderModel.Statuses.canceled;
            default:
                return status;
        }
    }

    get isCanceled() {
        return this.canceledTime !== null;
    }

    get isActive() {
        return this.status === OrderModel.Statuses.active;
    }

    get isPending() {
        return this.status === OrderModel.Statuses.pending;
    }

    get isPast() {
        return ![
            OrderModel.Statuses.active,
            OrderModel.Statuses.pending,
        ].includes(this.status);
    }

    get isOneTime() {
        return this.orderType === OrderModel.Types.oneTime;
    }

    getPayload() {
        const billingAddress = this.billingAddress
            ? this.billingAddress.getPayload()
            : undefined;

        const invoiceTimeShift = this.invoiceTimeShift.getPayload() || null;

        const recurringInterval = this.recurringInterval
            ? {...this.recurringInterval}
            : null;

        const periodAnchorInstruction = recurringInterval
            ? recurringInterval.periodAnchorInstruction
            : null;

        if (periodAnchorInstruction) {
            recurringInterval.periodAnchorInstruction = periodAnchorInstruction.getPayload();
        }

        return {
            orderType: this.orderType,
            customerId: this.customerId,
            items: this.items,
            websiteId: this.websiteId,
            deliveryAddress: this.deliveryAddress.getPayload(),
            billingAddress,
            invoiceTimeShift,
            recurringInterval,
            autopay: this.autopay,
            trial: this.trial,
            riskMetadata: this.riskMetadata,
            startTime: this.startTime,
            renewalTime: this.renewalTime,
            endTime: this.endTime,
            canceledTime: this.canceledTime,
            customFields: this.customFields,
        };
    }
}
